import React, { useState, useCallback, useEffect } from 'react';

import { useTranslate } from '@tolgee/react';

import useWindowSize from '@components/common/hooks/useWindowSize';
import Text from '@components/common/Text/Text';
import { M_DEVICE, S_DEVICE } from '@components/lpc/Constants';
import CircleAvatar from '@components/lpc/Navbar/CircleAvatar/CircleAvatar';
import NotificationAction from '@components/lpc/Pot/MainModule/Activity/Notification/NotficationAction/NotificationAction';
import { AvatarType } from '@propTypes/userTypes';
import { getDurationFromNow } from '@services/utils/DateService';
import { formatCurrency } from '@services/utils/StringService';

import css from './NotificationCard.module.scss';

type NotificationCardProps = {
    avatar: AvatarType;
    firstName: string;
    lastName: string;
    orderId: string;
    hideName: boolean;
    hideContribution: boolean;
    contribution: number;
    message: string;
    isOrganiser: boolean;
    type: string;
    creationDate: string;
    likesCount?: number;
    isLiked?: boolean;
    personRole: string;
    potRef: string;
    id: string;
    onUpdateActivities: () => void;
    setOpenParticipantModal?: (value: boolean) => void;
};

const NotificationCard = ({
    avatar,
    firstName,
    creationDate,
    isOrganiser,
    lastName,
    likesCount,
    type,
    isLiked,
    personRole,
    potRef,
    onUpdateActivities,
    orderId,
    contribution,
    message,
    id,
    hideName,
    hideContribution,
    setOpenParticipantModal,
}: NotificationCardProps) => {
    const { t } = useTranslate(['lpc-pot']);
    const { width } = useWindowSize();

    const [timeFromNow, setTimeFromNow] = useState(null);

    const getTimeFromNow = useCallback(() => {
        // @ts-ignore
        const { years, months, days, hours, minutes } = getDurationFromNow(creationDate || '');
        if (years == 0 && months == 0 && days === 0 && hours == 0 && minutes == 0) {
            setTimeFromNow(t('activity.just-now'));
        } else if (years == 0 && months == 0 && days == 0 && hours == 0 && minutes > 0) {
            setTimeFromNow(
                t(`activity.minute-ago${Math.abs(minutes) > 1 ? '_plural' : ''}`, { count: Math.abs(minutes) }),
            );
        } else if (years == 0 && months == 0 && days == 0 && hours > 0) {
            setTimeFromNow(t(`activity.hour-ago${Math.abs(hours) > 1 ? '_plural' : ''}`, { count: Math.abs(hours) }));
        } else if (days < 0 && (hours > 0 || minutes > 0)) {
            setTimeFromNow(t(`activity.day-ago${Math.abs(days) > 1 ? '_plural' : ''}`, { count: Math.abs(days - 1) }));
        } else if (days < 0) {
            setTimeFromNow(t(`activity.day-ago${Math.abs(days) > 1 ? '_plural' : ''}`, { count: Math.abs(days) }));
        } else if (months < 0) {
            setTimeFromNow(
                t(`activity.month-ago${Math.abs(months) > 1 ? '_plural' : ''}`, { count: Math.abs(months) }),
            );
        } else if (years < 0) {
            setTimeFromNow(t(`activity.year-ago${Math.abs(years) > 1 ? '_plural' : ''}`, { count: Math.abs(years) }));
        }
    }, [creationDate]);

    useEffect(() => {
        getTimeFromNow();
    }, [getTimeFromNow]);

    return (
        <div className={css.notification}>
            <>
                {isOrganiser && type === 'MESSAGE' && (
                    <div className={css.organiser}>
                        <Text variant={'caption_02'} color={'brand-primary'}>
                            {t('activity.message-from-organiser')}
                        </Text>
                    </div>
                )}
                <div className={css.information}>
                    <NotificationAction
                        lastName={lastName}
                        firstName={firstName}
                        type={type}
                        onUpdateActivities={onUpdateActivities}
                        contribution={contribution}
                        orderId={orderId}
                        id={id}
                        potRef={potRef}
                        liked={isLiked}
                        counter={likesCount}
                        personRole={personRole}
                        setOpenParticipantModal={setOpenParticipantModal}
                    />

                    <CircleAvatar
                        avatar={avatar}
                        height={width > M_DEVICE ? '45px' : '40px'}
                        width={width > M_DEVICE ? '45px' : '40px'}
                    />
                    <div className={css.information__content}>
                        <div className={css.information__content__user}>
                            {hideName && type !== 'MESSAGE' ? (
                                <Text variant={'caption_00'} color={'txt-primary'}>
                                    {t('activity.participation-anonymous')}
                                </Text>
                            ) : (
                                <Text variant={'caption_00'} color={'txt-primary'}>
                                    {firstName} {lastName}
                                </Text>
                            )}
                            {width > S_DEVICE && (
                                <Text
                                    customClass={css.information__content__user__date}
                                    variant={'body_02'}
                                    color={'txt-secondary'}
                                >
                                    {' • '}
                                    {timeFromNow}
                                </Text>
                            )}
                        </div>
                        <Text customClass={css.information__content__type} variant={'body_02'} color={'txt-secondary'}>
                            {width <= S_DEVICE && ' • ' + timeFromNow}
                        </Text>
                    </div>
                </div>

                <div className={css.information__content__desc}>
                    {type === 'MESSAGE' && (
                        <Text variant={'body_02'} color={'txt-primary'} html htmlText={message}></Text>
                    )}

                    {type === 'CONTRIBUTION' && (
                        <>
                            <Text
                                htmlText={
                                    hideContribution
                                        ? t('activity.participation-hidden')
                                        : t('activity.participation-desc-1', {
                                              participationAmount: formatCurrency(contribution),
                                          })
                                }
                                variant={'body_02'}
                                color={'txt-primary'}
                                html
                            />
                        </>
                    )}
                </div>
            </>
        </div>
    );
};

export default NotificationCard;
